import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { postbackReasonText } from '../lib/postbackReasons';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

/*
  Click and conversion check (plan: stage 1, stage 3 item 2). The client opens
  the campaign link once (a real click), then sends a test conversion for that
  click from the affiliate network. The dialog polls
  GET /campaigns/{id}/conversion-check and shows each step as it happens.
*/

const POLL_MS = 4000;
const MAX_MS = 15 * 60 * 1000;
const HINT_AFTER_MS = 3 * 60 * 1000;

export default function ConversionCheck({ campaignId, open, onOpenChange }) {
  const { t } = useT();
  const [run, setRun] = useState(0);
  const [data, setData] = useState(null);
  const [clickSeenAt, setClickSeenAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const sinceRef = useRef(null);
  const clickIdRef = useRef(null);

  const start = useCallback(() => {
    sinceRef.current = null;
    clickIdRef.current = null;
    setData(null);
    setClickSeenAt(null);
    setRun((n) => n + 1);
  }, []);

  useEffect(() => {
    if (open) start();
  }, [open, start]);

  useEffect(() => {
    if (!open || !run || !campaignId) return undefined;
    let alive = true;
    let timer = null;
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const params = {};
        if (sinceRef.current) params.since = sinceRef.current;
        if (clickIdRef.current) params.click_id = clickIdRef.current;
        const res = await axios.get(`${API}/campaigns/${campaignId}/conversion-check`, { params });
        if (!alive) return false;
        // The server starts the check on its own clock: no client clock skew.
        if (!sinceRef.current) sinceRef.current = res.data?.started_at || null;
        if (res.data?.click?.click_id && !clickIdRef.current) {
          clickIdRef.current = res.data.click.click_id;
          setClickSeenAt(Date.now());
        }
        setData(res.data);
        setNow(Date.now());
        return res.data?.state === 'received';
      } catch (_e) {
        return false;
      }
    };
    const loop = async () => {
      const done = await tick();
      if (!alive || done || Date.now() - startedAt > MAX_MS) return;
      timer = setTimeout(loop, POLL_MS);
    };
    loop();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [open, run, campaignId]);

  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(t('Copied'));
    } catch (_e) {
      /* clipboard blocked: the address stays visible to copy by hand */
    }
  };

  const state = data?.state;
  const waitingLong = state === 'waiting_conversion' && clickSeenAt && now - clickSeenAt > HINT_AFTER_MS;
  const conv = data?.conversion;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('Check click and conversion')}</DialogTitle>
          <DialogDescription>
            {t('The tracker waits for a real click on your campaign link, then for the conversion your affiliate network reports for this click.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 text-sm" data-testid="conversion-check">
          <section className="space-y-2">
            <div className="font-medium">{t('1. Open the campaign link')}</div>
            {data?.tracking_url ? <p className="break-all font-mono text-xs">{data.tracking_url}</p> : null}
            {data && !data.campaign_active ? (
              <p className="text-destructive">{t('The campaign is turned off: the link does not open. Turn the campaign on first.')}</p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={!data?.tracking_url}
                onClick={() => window.open(data.tracking_url, '_blank', 'noopener')}
              >
                <ExternalLink className="mr-1 h-4 w-4" />
                {t('Open the link')}
              </Button>
              <span className="text-xs text-muted-foreground">{t('This visit counts in your statistics as a normal click.')}</span>
            </div>
            {!data?.click ? (
              <p className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('Waiting for the click…')}
              </p>
            ) : null}
          </section>

          {data?.click ? (
            <section className="space-y-2">
              <div className="font-medium">{t('2. Click received: {click_id}', { click_id: data.click.click_id })}</div>
              <p className="text-muted-foreground">
                {t('Send a test conversion for this click from your affiliate network account. If the network has no such button, open the address below in the browser: the tracker takes it as a conversion from the network. It counts in your statistics.')}
              </p>
              {data.postback_url ? (
                <div className="flex items-start gap-2">
                  <code className="flex-1 break-all rounded bg-muted p-2 text-xs">{data.postback_url}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(data.postback_url)} aria-label={t('Copy')}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
              {state !== 'received' ? (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('Waiting for the conversion…')}
                </p>
              ) : null}
              {data.last_inbound && data.last_inbound.outcome !== 'accepted' ? (
                <p className="text-destructive">
                  {t('The network sent a postback for this click, but: {reason}', {
                    reason: postbackReasonText(t, data.last_inbound.reason),
                  })}
                </p>
              ) : null}
              {waitingLong ? (
                <p className="text-muted-foreground">
                  {t('The conversion has not arrived yet. Check that the network account has the postback URL of this campaign and that it passes the click ID of this click.')}
                </p>
              ) : null}
            </section>
          ) : null}

          {state === 'received' && conv ? (
            <section className="flex items-start gap-2 font-medium text-primary">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              <span>
                {t('3. Conversion received: status {status}, payout {revenue} {currency}.', {
                  status: conv.status,
                  revenue: conv.revenue,
                  currency: conv.currency,
                })}
              </span>
            </section>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={start}>
              {t('Start again')}
            </Button>
            <Button size="sm" onClick={() => onOpenChange(false)}>
              {t('Close')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
